/**
 * 管理配置与权限系统
 * 包含：数据版本、角色定义、细粒度权限、菜单权限映射、管理员账户、配置项
 */

/** 当前数据结构版本 */
export const DATA_VERSION = '2.0.0'

/** JSON Schema 版本 */
export const SCHEMA_VERSION = '1.0.0'

/** 角色定义 */
export const ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin'
}

/** 权限定义 —— 细粒度操作权限 */
export const PERMISSIONS = {
  // 话题管理
  VIEW_TOPIC: 'view_topic',
  CREATE_TOPIC: 'create_topic',
  EDIT_TOPIC: 'edit_topic',
  DELETE_TOPIC: 'delete_topic',
  CONFIG_TOPIC: 'config_topic',

  // 案例管理
  VIEW_CASE: 'view_case',
  CREATE_CASE: 'create_case',
  EDIT_CASE: 'edit_case',
  DELETE_CASE: 'delete_case',
  CONFIG_CASE: 'config_case',

  // 专家管理
  VIEW_EXPERT: 'view_expert',
  CREATE_EXPERT: 'create_expert',
  EDIT_EXPERT: 'edit_expert',
  DELETE_EXPERT: 'delete_expert',
  CONFIG_EXPERT: 'config_expert',

  // 场景题库
  VIEW_SCENARIO: 'view_scenario',
  CREATE_SCENARIO: 'create_scenario',
  EDIT_SCENARIO: 'edit_scenario',
  DELETE_SCENARIO: 'delete_scenario',

  // 诊断模板与配置
  VIEW_DIAGNOSIS: 'view_diagnosis',
  EDIT_DIAGNOSIS: 'edit_diagnosis',
  CONFIG_DIAGNOSIS: 'config_diagnosis',

  // 投稿审核
  VIEW_CONTRIBUTION: 'view_contribution',
  REVIEW_CONTRIBUTION: 'review_contribution',

  // 全局配置
  VIEW_CONFIG: 'view_config',
  EDIT_CONFIG: 'edit_config',

  // 权限管理（仅超级管理员）
  VIEW_ADMIN: 'view_admin',
  CREATE_ADMIN: 'create_admin',
  EDIT_ADMIN: 'edit_admin',
  DELETE_ADMIN: 'delete_admin',

  // 审计日志
  VIEW_AUDIT: 'view_audit',

  // 用户列表
  VIEW_USER: 'view_user',
}

/** 菜单权限映射 —— 进入某个菜单需要什么权限 */
export const MENU_PERMISSIONS = {
  'topics': PERMISSIONS.VIEW_TOPIC,
  'cases': PERMISSIONS.VIEW_CASE,
  'experts': PERMISSIONS.VIEW_EXPERT,
  'scenarios': PERMISSIONS.VIEW_SCENARIO,
  'diagnosis-templates': PERMISSIONS.VIEW_DIAGNOSIS,
  'matching-rules': PERMISSIONS.VIEW_DIAGNOSIS,
  'contributions': PERMISSIONS.VIEW_CONTRIBUTION,
  'config': PERMISSIONS.VIEW_CONFIG,
  'permissions': PERMISSIONS.VIEW_ADMIN,
  'audit': PERMISSIONS.VIEW_AUDIT,
  'users': PERMISSIONS.VIEW_USER,
}

/** 权限分组 —— 用于权限配置界面的树形结构 */
export const PERMISSION_GROUPS = [
  {
    label: '话题管理',
    permissions: [
      { key: PERMISSIONS.VIEW_TOPIC, label: '查看' },
      { key: PERMISSIONS.CREATE_TOPIC, label: '新增' },
      { key: PERMISSIONS.EDIT_TOPIC, label: '编辑' },
      { key: PERMISSIONS.DELETE_TOPIC, label: '删除' },
      { key: PERMISSIONS.CONFIG_TOPIC, label: '运营配置' },
    ]
  },
  {
    label: '案例管理',
    permissions: [
      { key: PERMISSIONS.VIEW_CASE, label: '查看' },
      { key: PERMISSIONS.CREATE_CASE, label: '新增' },
      { key: PERMISSIONS.EDIT_CASE, label: '编辑' },
      { key: PERMISSIONS.DELETE_CASE, label: '删除' },
      { key: PERMISSIONS.CONFIG_CASE, label: '运营配置' },
    ]
  },
  {
    label: '专家管理',
    permissions: [
      { key: PERMISSIONS.VIEW_EXPERT, label: '查看' },
      { key: PERMISSIONS.CREATE_EXPERT, label: '新增' },
      { key: PERMISSIONS.EDIT_EXPERT, label: '编辑' },
      { key: PERMISSIONS.DELETE_EXPERT, label: '删除' },
      { key: PERMISSIONS.CONFIG_EXPERT, label: '运营配置' },
    ]
  },
  {
    label: '场景题库',
    permissions: [
      { key: PERMISSIONS.VIEW_SCENARIO, label: '查看' },
      { key: PERMISSIONS.CREATE_SCENARIO, label: '新增' },
      { key: PERMISSIONS.EDIT_SCENARIO, label: '编辑' },
      { key: PERMISSIONS.DELETE_SCENARIO, label: '删除' },
    ]
  },
  {
    label: '诊断模板与配置',
    permissions: [
      { key: PERMISSIONS.VIEW_DIAGNOSIS, label: '查看' },
      { key: PERMISSIONS.EDIT_DIAGNOSIS, label: '编辑' },
      { key: PERMISSIONS.CONFIG_DIAGNOSIS, label: '运营配置' },
    ]
  },
  {
    label: '投稿审核',
    permissions: [
      { key: PERMISSIONS.VIEW_CONTRIBUTION, label: '查看' },
      { key: PERMISSIONS.REVIEW_CONTRIBUTION, label: '审核' },
    ]
  },
  {
    label: '全局配置',
    permissions: [
      { key: PERMISSIONS.VIEW_CONFIG, label: '查看' },
      { key: PERMISSIONS.EDIT_CONFIG, label: '编辑' },
    ]
  },
  {
    label: '权限管理',
    permissions: [
      { key: PERMISSIONS.VIEW_ADMIN, label: '查看' },
      { key: PERMISSIONS.CREATE_ADMIN, label: '新增' },
      { key: PERMISSIONS.EDIT_ADMIN, label: '编辑' },
      { key: PERMISSIONS.DELETE_ADMIN, label: '删除' },
    ]
  },
  {
    label: '审计日志',
    permissions: [
      { key: PERMISSIONS.VIEW_AUDIT, label: '查看' },
    ]
  },
  {
    label: '用户列表',
    permissions: [
      { key: PERMISSIONS.VIEW_USER, label: '查看' },
    ]
  },
]

/** 菜单顺序 —— 用于无权限时的重定向 */
export const MENU_ORDER = [
  'topics', 'cases', 'experts', 'scenarios',
  'diagnosis-templates', 'matching-rules',
  'contributions', 'users', 'config', 'permissions', 'audit'
]

/** 默认管理员账户 */
export const DEFAULT_ADMINS = [
  {
    id: 'super-admin',
    username: 'superadmin',
    password: 'admin123',
    role: ROLES.SUPER_ADMIN,
    permissions: Object.values(PERMISSIONS),
    dataScope: 'all',
    status: 'active',
    createdAt: Date.now(),
  }
]

/** 旧版角色权限映射（兼容用） */
export const ROLE_PERMISSIONS = {
  [ROLES.USER]: ['view_content', 'interact', 'contribute'],
  [ROLES.ADMIN]: Object.values(PERMISSIONS),
  [ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS),
}

/** 持久化存储的 Key 列表 */
export const STORAGE_KEYS = {
  CONTEXT: 'saodiseng_context',
  USER_STATS: 'saodiseng_user_stats',
  HISTORY: 'management_history',
  STUDY_RECORDS: 'saodiseng_study_records',
  PRACTICE_RECORDS: 'saodiseng_practice_records',
  DEVICE_ID: 'saodiseng_device_id',
  LAST_BACKUP: 'saodiseng_last_backup',
  AUDIT_LOGS: 'saodiseng_audit_logs',
  CONFIG: 'saodiseng_config',
  CONTRIBUTIONS: 'saodiseng_contributions',
  TOPICS: 'saodiseng_topics',
  CASES: 'saodiseng_cases',
  EXPERTS: 'saodiseng_experts',
  ADMINS: 'saodiseng_admins',
  CURRENT_ADMIN_ID: 'saodiseng_current_admin_id',
}

/** 积分权重配置（运营可调） */
export const SCORING_CONFIG = {
  weights: {
    click: 1,
    play: 2,
    bookmark: 5,
    like: 5,
    comment: 5
  }
}

/** 专家匹配算法权重配置（运营可调） */
export const MATCHING_CONFIG = {
  weights: {
    topicMatch: 0.4,
    styleMatch: 0.3,
    stageMatch: 0.3
  }
}

/** 风险评分阈值配置（运营可调） */
export const RISK_CONFIG = {
  thresholds: {
    extreme: 85,
    high: 70,
    medium: 50
  }
}

/** 功能开关配置（运营可调） */
export const FEATURE_FLAGS = {
  enableExpertBooking: true,
  enableUserContribution: true,
  enableAiDiagnosis: true,
  enablePractice: true,
  enableComment: true
}

/** 工具函数：是否是超级管理员 */
export function isSuperAdmin(role) {
  return role === ROLES.SUPER_ADMIN
}

/** 工具函数：兼容旧版角色名 */
export function normalizeRole(role) {
  if (role === 'operator') return ROLES.ADMIN
  return role
}

/** 工具函数：获取第一个有权限的菜单路由 */
export function findFirstPermittedMenu(perms) {
  for (const route of MENU_ORDER) {
    if (perms.includes(MENU_PERMISSIONS[route])) {
      return route
    }
  }
  return null
}

/** 备份数据的 JSON Schema 形状校验（简化版） */
export const BACKUP_SCHEMA = {
  requiredFields: ['version', 'timestamp', 'deviceId', 'data'],
  dataFields: ['context', 'userStats', 'history', 'studyRecords', 'practiceRecords']
}

/**
 * 校验备份数据格式
 * @param {object} backup - 备份数据对象
 * @returns {{valid: boolean, error?: string}}
 */
export function validateBackup(backup) {
  if (!backup || typeof backup !== 'object') {
    return { valid: false, error: '备份数据必须是对象' }
  }

  for (const field of BACKUP_SCHEMA.requiredFields) {
    if (!(field in backup)) {
      return { valid: false, error: `缺少必填字段: ${field}` }
    }
  }

  if (typeof backup.version !== 'string') {
    return { valid: false, error: 'version 必须是字符串' }
  }

  const backupMajor = backup.version.split('.')[0]
  const currentMajor = DATA_VERSION.split('.')[0]
  if (backupMajor !== currentMajor) {
    return { valid: false, error: `数据版本不兼容: 备份 v${backup.version}, 当前 v${DATA_VERSION}` }
  }

  if (!backup.data || typeof backup.data !== 'object') {
    return { valid: false, error: 'data 必须是对象' }
  }

  for (const field of BACKUP_SCHEMA.dataFields) {
    const value = backup.data[field]
    if (value !== null && value !== undefined) {
      try {
        const parsed = JSON.parse(value)
        if (field === 'history' || field === 'studyRecords' || field === 'practiceRecords') {
          if (!Array.isArray(parsed)) {
            return { valid: false, error: `data.${field} 必须是数组` }
          }
        } else if (field === 'context' || field === 'userStats') {
          if (typeof parsed !== 'object' || parsed === null) {
            return { valid: false, error: `data.${field} 必须是对象` }
          }
        }
      } catch {
        return { valid: false, error: `data.${field} 必须是有效的 JSON 字符串` }
      }
    }
  }

  return { valid: true }
}

/** 版本迁移映射（未来数据升级时扩展） */
export const MIGRATIONS = {
  // '1.0.0 -> 2.0.0': (data) => { ... }
}
